import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material'

import { useStaffApplications } from 'api/auth'
import AlertBottom from 'components/common/alertBottom'
import { formatReferenceCopy } from 'utils/typography'
import {
  useAnswerClarificationMutation,
  useConfirmCheckItemTnvedChangesMutation,
  usePostCheckItemCommentMutation,
  useAssignExecutorMutation,
  useAssignManagerMutation,
  useCompletePreparationMutation,
  usePostAdminCheckItemCommentMutation,
  useDeclineProposalMutation,
  useGetAdminApplicationQuery,
  useGetApplicationQuery,
  useListExecutorCompaniesQuery,
  useMetaCheckItemStatusesQuery,
  useMetaOutcomesQuery,
  usePublishProposalMutation,
  useRequestContactExchangeMutation,
  useRequestExecutorTransferMutation,
  useRevealContactsMutation,
  useSaveClientContactsMutation,
  useSaveExecutorContactsMutation,
  useSaveProposalDraftMutation,
  useUpdateCheckItemMutation,
  useUploadCheckItemDocumentMutation,
  useDeleteCheckItemDocumentMutation,
  useUpdateOutcomeMutation,
} from 'api/applications'
import {
  applicationsListPath,
  resolveApplicationManagerView,
} from 'utils/applicationRoutes'
import {
  useApplication,
  useApplicationCheckStatuses,
  useApplicationOutcomes,
  useApplicationExecutorCompanies,
} from 'api/applications'
import { defaultProposalCurrencyCode } from './proposal-currencies'
import {
  buildDefaultProposalServiceLines,
} from './proposal-service-line-utils'

import ApplicationDetailViewMigration from './detail-view-migration'
import ApplicationDetailHeader from './detail-header'
import ApplicationDetailContent from './detail-content'
import ApplicationCalculationSummaryPanel from './components/calculation-summary-panel'

import {
  clientContactFormFromApp,
  executorContactFormFromApp,
} from './application-contacts-fields'
import { formatApplicationActionError } from './reveal-contacts-form'

import './detail.scss'

const ApplicationDetailPage = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [ searchParams ] = useSearchParams()
  const { publicId = '' } = useParams()
  const canManageApplications = useStaffApplications()
  const managerView = resolveApplicationManagerView({
    canManageApplications: canManageApplications,
    searchParams: searchParams,
    locationState: location.state as ApplicationDetailLocationState | null,
    pathname: location.pathname,
  })

  const clientQuery = useGetApplicationQuery(publicId, { skip: !publicId || managerView })
  const adminQuery = useGetAdminApplicationQuery(publicId, { skip: !publicId || !managerView })
  const active = managerView ? adminQuery : clientQuery
  const app = useApplication({ publicId, managerView })

  const checkStatuses = useApplicationCheckStatuses()
  const outcomes = useApplicationOutcomes()
  const companiesData = useApplicationExecutorCompanies()
  useMetaCheckItemStatusesQuery(undefined, { skip: !publicId })
  useMetaOutcomesQuery(undefined, { skip: !managerView })
  useListExecutorCompaniesQuery(
    { limit: 100, search: '' },
    { skip: !managerView },
  )

  const [ answerClarification ] = useAnswerClarificationMutation()
  const [ confirmCheckItemTnvedChanges ] = useConfirmCheckItemTnvedChangesMutation()
  const [ postCheckItemComment ] = usePostCheckItemCommentMutation()
  const [ postAdminCheckItemComment ] = usePostAdminCheckItemCommentMutation()
  const [ requestTransfer ] = useRequestExecutorTransferMutation()
  const [ requestContacts ] = useRequestContactExchangeMutation()
  const [ declineProposal ] = useDeclineProposalMutation()
  const [ assignManager, { isLoading: isAssigningManager } ] = useAssignManagerMutation()
  const [ assignExecutor ] = useAssignExecutorMutation()
  const [ updateCheckItem ] = useUpdateCheckItemMutation()
  const [ uploadCheckItemDocument ] = useUploadCheckItemDocumentMutation()
  const [ deleteCheckItemDocument ] = useDeleteCheckItemDocumentMutation()
  const [ completePreparation ] = useCompletePreparationMutation()
  const [ saveProposal ] = useSaveProposalDraftMutation()
  const [ publishProposal ] = usePublishProposalMutation()
  const [ revealContacts ] = useRevealContactsMutation()
  const [ saveClientContacts ] = useSaveClientContactsMutation()
  const [ saveExecutorContacts ] = useSaveExecutorContactsMutation()
  const [ updateOutcome ] = useUpdateOutcomeMutation()

  const [ executorId, setExecutorId ] = useState<number | ''>('')
  const [ executorChangeReason, setExecutorChangeReason ] = useState('')
  const [ contactForm, setContactForm ] = useState<RevealContactsFormValues>({
    executor_manager_name: '',
    executor_manager_phone: '',
    executor_manager_email: '',
  })
  const [ clientContactForm, setClientContactForm ] = useState<ClientContactFormValues>({
    client_contact_name: '',
    client_contact_phone: '',
    client_contact_email: '',
  })
  const [ proposalForm, setProposalForm ] = useState({
    amount: '',
    currency_code: 'USD',
    terms: '',
    service_lines: buildDefaultProposalServiceLines(null),
  })
  const [ outcome, setOutcome ] = useState('')
  const [ outcomeNote, setOutcomeNote ] = useState('')
  const [ error, setError ] = useState<string | null>(null)
  const [ successMessage, setSuccessMessage ] = useState<string | null>(null)
  const [ isCalculationCollapsed, setIsCalculationCollapsed ] = useState(true)

  useEffect(() => {
    setIsCalculationCollapsed(true)
  }, [ publicId, managerView ])

  const companyOptions = companiesData?.results ?? []

  const suggestedExecutor = useMemo(() => {
    if (!app?.suggested_executor_company_id) return ''
    return String(app.suggested_executor_company_id)
  }, [ app ])

  useEffect(() => {
    if (!app) {
      return
    }
    if (app.executor_company_id) {
      setExecutorId(app.executor_company_id)
    } else if (suggestedExecutor) {
      setExecutorId(Number(suggestedExecutor))
    }
    if (app.proposal || app.proposal_service_line_baselines?.length) {
      const serviceLines = buildDefaultProposalServiceLines(
        app.proposal,
        app.proposal_service_line_baselines,
      )
      setProposalForm({
        amount: app.proposal?.amount || '',
        currency_code: defaultProposalCurrencyCode(app.original_calculation_currency),
        terms: app.proposal?.terms || '',
        service_lines: serviceLines,
      })
    }
    if (app.outcome) setOutcome(app.outcome)
    if (app.outcome_note) setOutcomeNote(app.outcome_note)
    setClientContactForm(clientContactFormFromApp(app))
    setContactForm(executorContactFormFromApp(app))
  }, [ app, suggestedExecutor ])

  const runAction: ApplicationRunAction = (fn, successMessageKey) => {
    const action = (async () => {
      setError(null)
      setSuccessMessage(null)
      try {
        await fn()
        setSuccessMessage(t(successMessageKey))
        active.refetch()
      } catch (e: unknown) {
        const err = e as { data?: unknown }
        setError(formatApplicationActionError({ data: err?.data, fallback: t('applications__text__actionError') }))
        throw e
      }
    })()

    action.catch(() => undefined)
    return action
  }

  if (!publicId) return null

  if (active.isLoading) {
    return (
      <Box className='application-details'>
        <CircularProgress />
      </Box>
    )
  }

  if (!app) {
    return (
      <main className='application-details' lang={i18n.language}>
        <Alert severity='error'><Typography variant='body1'>{t('applications__text__notFound')}</Typography></Alert>
        <Button sx={{ marginTop: 2 }} onClick={() => navigate(applicationsListPath())}>
          <Typography variant='body2'>{t('applications__text__backToList')}</Typography>
        </Button>
      </main>
    )
  }

  const detailData: ApplicationDetailContentData = {
    app,
    managerView,
    checkStatuses,
    runAction,
    answerClarification,
    publicId,
    requestTransfer,
    requestContacts,
    declineProposal,
    outcomes,
    companyOptions,
    executorId,
    setExecutorId,
    executorChangeReason,
    setExecutorChangeReason,
    proposalForm,
    setProposalForm,
    contactForm,
    setContactForm,
    clientContactForm,
    setClientContactForm,
    outcome,
    setOutcome,
    outcomeNote,
    setOutcomeNote,
    assignManager,
    isTakingWork: isAssigningManager || active.isFetching,
    assignExecutor,
    updateCheckItem,
    confirmCheckItemTnvedChanges,
    postCheckItemComment,
    postAdminCheckItemComment,
    uploadCheckItemDocument,
    deleteCheckItemDocument,
    completePreparation,
    saveProposal,
    publishProposal,
    revealContacts,
    saveClientContacts,
    saveExecutorContacts,
    updateOutcome,
  }

  const tnvedCheckItem = app.check_items.find((item) => item.kind === 'tnved_code')

  const header = (
    <div className='application-details__header'>
      <ApplicationDetailHeader
        app={app}
        managerView={managerView}
        canManageApplications={canManageApplications}
      />
    </div>
  )

  const content = (
    <div className='application-details__main'>
      <ApplicationDetailContent data={detailData} />
    </div>
  )

  const calculation = app.calculation_number && (
    <ApplicationCalculationSummaryPanel
      calculationNumber={app.calculation_number}
      tnvedReviews={tnvedCheckItem?.tnved_reviews}
      tnvedChangesConfirmed={Boolean(tnvedCheckItem?.tnved_changes_confirmed)}
      calculationComparison={app.calculation_comparison}
      calculationSnapshotProductCodes={app.calculation_snapshot_product_codes}
      hasProposalAdjustment={Boolean(app.proposal_adjusted_summary?.total_amount)}
      calculationCurrencyCode={app.original_calculation_currency}
      managerView={managerView}
      isCollapsed={isCalculationCollapsed}
      onToggle={() => setIsCalculationCollapsed((collapsed) => !collapsed)}
    />
  )

  const managerLayoutClass = isCalculationCollapsed
    ? 'application-details__layout application-details__layout_manager application-details__layout_collapsed'
    : 'application-details__layout application-details__layout_manager'

  return (
    <main className='application-details' lang={i18n.language}>
      <AlertBottom
        open={Boolean(successMessage) || Boolean(error)}
        isError={Boolean(error)}
        message={formatReferenceCopy(error || successMessage || '')}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        onClose={() => {
          setError(null)
          setSuccessMessage(null)
        }}
      />
      <ApplicationDetailViewMigration />
      {managerView ? (
        <>
          {header}
          <div className={managerLayoutClass}>
            {content}
            {calculation}
          </div>
        </>
      ) : (
        <div className='application-details__layout'>
          {header}
          {content}
          {calculation}
        </div>
      )}
    </main>
  )
}

export default ApplicationDetailPage
